import { matchPath } from "react-router";
import { REPORT_QUERY_PARAMS } from "@sscs/report/constants";
import { SDLCMetadata } from "@sscs/types";
import { getDirnameFromComponentPath } from "@sscs/utils";
import {
  FULL_REPORT_PATH,
  REPORT_RETURN_TO_PREFIX,
  REPORT_TYPE_REPRO,
  REPORT_TYPE_VERSION,
} from "Constants";
import { LatestReportRequest } from "Types";

export const generateReportKey = (details: LatestReportRequest): string => {
  return JSON.stringify(details, Object.keys(details).sort());
};

export const parseReportKey = (reportKey: string): LatestReportRequest => {
  return JSON.parse(reportKey);
};

export const generateReportReturnToKey = (key: string) => {
  return `${REPORT_RETURN_TO_PREFIX}:${key}`;
};

export const getReportKeyFromPath = (path: string): string | null => {
  const { pathname, searchParams } = new URL(path, window.location.origin);
  const reportMatch = matchPath(
    { path: `/${FULL_REPORT_PATH}`, end: false },
    pathname,
  );

  if (!reportMatch) return null;

  const {
    groupId = "",
    projectName = "",
    packageName = "",
    version = "",
  } = reportMatch.params;

  return generateReportKey({
    groupId: decodeURIComponent(groupId),
    projectName: decodeURIComponent(projectName),
    packageName: decodeURIComponent(packageName),
    version: decodeURIComponent(version),
    build: searchParams.get(REPORT_QUERY_PARAMS.build),
    diffWith: searchParams.get(REPORT_QUERY_PARAMS.diff_with) ?? undefined,
  });
};

type ReportSearchParams = {
  isRepro: boolean | undefined;
  diffWith?: string;
  riskCategory?: SDLCMetadata.assessment_type_t;
  riskViolations?: string[];
  existingParams?: string;
  componentPath?: string;
};

export const generateReportSearchParams = ({
  isRepro,
  diffWith,
  riskCategory,
  riskViolations,
  existingParams,
  componentPath,
}: ReportSearchParams): string => {
  const build = isRepro ? REPORT_TYPE_REPRO : REPORT_TYPE_VERSION;
  const params = new URLSearchParams(existingParams);

  if (isRepro) {
    params.set(REPORT_QUERY_PARAMS.build, build);
  }

  if (diffWith) {
    params.set(REPORT_QUERY_PARAMS.diff_with, diffWith);
  }

  if (riskCategory) {
    params.set(REPORT_QUERY_PARAMS.risk_category, riskCategory);
  }

  if (riskViolations) {
    params.set(REPORT_QUERY_PARAMS.risk_violations, riskViolations.join(","));
  }

  if (componentPath) {
    params.set(
      REPORT_QUERY_PARAMS.path,
      `${getDirnameFromComponentPath(componentPath)}/`,
    );
  }

  return params.toString();
};
